/**
 * Legal move + deployment generation. This layers the royalty rules on top of
 * the raw geometric movement from movement.ts: a capture is legal when
 * `canCapture` allows it, and — crucially for check/checkmate — a move is only
 * legal if it does not leave (or place) the mover's own King in check.
 */
import { inDeployZone, shift } from "./coords";
import { pieceDef } from "./pieces";
import { canCapture, isInCheck, isPrinceAlive } from "./rules";
import type { GameState, Piece, PieceTypeId, PlayerId, Square, Vector } from "./types";

export interface Move {
  readonly from: Square;
  readonly to: Square;
  readonly capture: boolean;
}

/** A board with the piece on `from` relocated to `to` (capturing whatever sits there). */
function boardAfterMove(
  board: readonly (Piece | null)[],
  from: Square,
  to: Square,
): (Piece | null)[] {
  const next = board.slice();
  next[to] = next[from] ?? null;
  next[from] = null;
  return next;
}

/**
 * Legal destinations for the piece on `from`, for the side to move. Pseudo-legal
 * moves from raw geometry are filtered twice: capture legality, then the
 * self-check rule (you may not make a move that leaves your own King attacked).
 */
export function legalMovesFrom(state: GameState, from: Square): Move[] {
  const piece = state.board[from];
  if (!piece || piece.owner !== state.turn || state.phase !== "playing") return [];

  const raw = pieceDef(piece.type).movement({ state, from, piece });
  // While its Prince still guards it, a King is inviolate — and likewise it may
  // not capture: it can only shuffle to empty squares until the Prince is gone.
  const kingCannotCapture = piece.type === "king" && isPrinceAlive(state, piece.owner);
  const moves: Move[] = [];
  for (const m of raw) {
    if (m.capture && (kingCannotCapture || !canCapture(state, piece.owner, m.to))) continue;
    // Reject any move that leaves the mover's own King in check.
    const probe: GameState = { ...state, board: boardAfterMove(state.board, from, m.to) };
    if (isInCheck(probe, piece.owner)) continue;
    moves.push({ from, to: m.to, capture: m.capture });
  }
  return moves;
}

/** Empty squares in `owner`'s deployment zone. */
export function legalDeploySquares(state: GameState, owner: PlayerId): Square[] {
  const squares: Square[] = [];
  for (let sq = 0; sq < state.board.length; sq++) {
    if (state.board[sq]) continue;
    if (inDeployZone(sq, owner, state.config)) squares.push(sq);
  }
  return squares;
}

/**
 * Empty squares a given piece type may be deployed onto. Most pieces are
 * restricted to the owner's home ranks; `placeAnywhere` pieces (the Hill) may
 * be deployed onto any empty square on the board.
 */
// Adjacent directions an Outpost can project its deployment node onto: N/S/E/W.
const NODE_OFFSETS: readonly Vector[] = [
  [0, 1],
  [0, -1],
  [1, 0],
  [-1, 0],
];

/** Deterministic "random" node direction for an outpost — changes each ply. */
function nodeDirection(square: Square, ply: number): number {
  const h = (Math.imul(square + 1, 2654435761) ^ Math.imul(ply + 1, 40503)) >>> 0;
  return h % 4;
}

/**
 * The deployment-node squares projected by `owner`'s Outposts this turn. Each
 * Outpost projects one node onto a random adjacent square (re-rolled every ply),
 * letting the owner deploy beyond their home ranks. Off-board projections yield
 * no node.
 */
export function outpostNodes(state: GameState, owner: PlayerId): Square[] {
  const nodes: Square[] = [];
  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (!p || p.owner !== owner || p.type !== "outpost") continue;
    const node = shift(sq, NODE_OFFSETS[nodeDirection(sq, state.ply)]!, state.config);
    if (node !== null && !nodes.includes(node)) nodes.push(node);
  }
  return nodes;
}

/** The second cell a two-cell piece occupies when anchored at `sq`, or null. */
export function footprintPartner(
  sq: Square,
  footprint: "h" | "v",
  config: GameState["config"],
): Square | null {
  return shift(sq, footprint === "h" ? [1, 0] : [0, 1], config);
}

export function legalDeploySquaresForPiece(
  state: GameState,
  owner: PlayerId,
  type: PieceTypeId,
): Square[] {
  const def = pieceDef(type);
  if (def.placeAnywhere === true) {
    const squares: Square[] = [];
    for (let sq = 0; sq < state.board.length; sq++) {
      if (state.board[sq]) continue;
      if (def.footprint) {
        const partner = footprintPartner(sq, def.footprint, state.config);
        if (partner === null || state.board[partner]) continue;
      }
      squares.push(sq);
    }
    return squares;
  }
  // Home ranks plus any empty Outpost deployment node.
  const squares = legalDeploySquares(state, owner);
  for (const node of outpostNodes(state, owner)) {
    if (!state.board[node] && !squares.includes(node)) squares.push(node);
  }
  return squares;
}

/**
 * Whether `player` is holding more cards than the hand limit and so must
 * discard down before doing anything else. A card that returns a piece to a
 * hand (Recall, Resurrection) can push it over; drawing cannot.
 */
export function mustDiscard(state: GameState, player: PlayerId): boolean {
  return state.players[player].hand.length > state.config.handLimit;
}

/** Every legal move for a side (used for checkmate/stalemate detection and AI). */
export function allLegalMoves(state: GameState, owner: PlayerId): Move[] {
  if (state.turn !== owner) return [];
  const moves: Move[] = [];
  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (p && p.owner === owner) moves.push(...legalMovesFrom(state, sq));
  }
  return moves;
}
